import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Roles } from '../../../models/roles';
import { AppService } from '../../../services/app.service';
import { AuthService } from '../../../services/auth.service';
import { ExchangesService } from '../../../services/exchanges.service';
import { LoggerService } from '../../../services/logger.service';
import { UserService } from '../../../services/users.service';

@Component({
  selector: 'app-add-exchange',
  templateUrl: './add-exchange.component.html',
  styleUrl: './add-exchange.component.scss',
})
export class AddExchangeComponent implements OnInit {
  readonly roles = Roles;
  @Input() currency = '';

  declaringExchange = false;
  showAddExchangeOptions = true;
  showDeclareExchange = false;

  declareExchangeForm = this.formBuilder.group({
    origin_user_id: [null as number | null, Validators.required],
    destination_user_id: [null as number | null, Validators.required],
    origin_currency: [null as string | null, Validators.required],
    destination_currency: [null as string | null, Validators.required],
    origin_amount: [null as number | null, Validators.required],
    destination_amount: [null as number | null, Validators.required],
    origin_wallet_id: [null as string | null, Validators.required],
    destination_wallet_id: [null as string | null, Validators.required],
  });

  constructor(
    private formBuilder: FormBuilder,
    private exchangesService: ExchangesService,
    private messageService: MessageService,
    private logger: LoggerService,
    public appService: AppService,
    public auth: AuthService,
    public userService: UserService,
  ) {}

  ngOnInit(): void {
    this.setupFormSync();

    if (!this.auth.hasRole(this.roles.Admin)) {
      if (this.canQuoteExchange) {
        this.openExchangeRateCalculator();
      } else {
        this.close();
      }
      return;
    }

    if (!this.userService.users?.length) {
      this.userService.getUsers().catch((err) => this.logger.log(err));
    }
  }

  private setupFormSync(): void {
    this.declareExchangeForm.controls.origin_currency.valueChanges.subscribe(() => {
      this.declareExchangeForm.controls.origin_wallet_id.setValue(null);
    });
    this.declareExchangeForm.controls.destination_currency.valueChanges.subscribe(() => {
      this.declareExchangeForm.controls.destination_wallet_id.setValue(null);
    });
  }

  close(): void {
    if (this.declaringExchange) return;
    this.appService.triggerAddExchange(false);
  }

  openExchangeRateCalculator(): void {
    if (!this.canQuoteExchange) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permiso requerido',
        detail: 'No tienes permisos para cotizar exchange.',
      });
      return;
    }

    this.appService.triggerAddExchange(false);
    this.appService.triggerExchangeRateCalculator(true, this.currency || null);
  }

  openDeclareExchange(): void {
    this.showAddExchangeOptions = false;
    this.showDeclareExchange = true;
    this.declareExchangeForm.reset();
  }

  showOptions(): void {
    if (this.declaringExchange) return;
    this.showDeclareExchange = false;
    this.showAddExchangeOptions = true;
    this.declareExchangeForm.reset();
  }

  getCurrencyOptions() {
    return (this.appService.config?.currencies ?? []).map((currency: any) => ({
      ...currency,
      label: `${currency.id} - ${currency.name || currency.shortname || currency.id}`,
      value: currency.id,
    }));
  }

  getWalletOptions(currency: string) {
    const configCurrency = this.appService.config?.currencies?.find(
      (el: any) => el.id === currency,
    );

    return (configCurrency?.subwallets ?? []).map((wallet: any) => ({
      ...wallet,
      label: wallet.shortname || wallet.name || wallet.id,
      id: wallet.id,
    }));
  }

  getUserOptions() {
    return (this.userService.users ?? []).map((user: any) => ({
      ...user,
      label: user.username,
      value: user.user_id,
    }));
  }

  declareExchange(): void {
    if (this.declaringExchange || this.declareExchangeForm.invalid) return;

    const value = this.declareExchangeForm.getRawValue();
    if (value.origin_user_id === value.destination_user_id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos inválidos',
        detail: 'Los usuarios deben ser distintos.',
      });
      return;
    }

    if (value.origin_currency === value.destination_currency) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos inválidos',
        detail: 'Las monedas deben ser distintas.',
      });
      return;
    }

    if (!this.userService.hasPlatformService('exchange:execute')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permiso requerido',
        detail: 'No tienes permisos para declarar exchange.',
      });
      return;
    }

    this.declaringExchange = true;
    this.exchangesService.declareExchange(value).then(
      () => {
        this.declaringExchange = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: 'Exchange declarado exitosamente.',
        });
        this.appService.triggerExchangeCreated();
        this.close();
      },
      (err) => {
        const body = err?.error ?? err;
        this.declaringExchange = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: body?.message || 'Ocurrió un error al declarar el exchange.',
        });
      },
    );
  }

  get canQuoteExchange(): boolean {
    return this.userService.hasPlatformService([
      'exchange:quote',
      'exchange:execute',
    ]);
  }
}
